import moment from "moment";

import { deserializeAddonValue } from "@/lib/utils";
import { Order, OrderItem, OrderCreate } from "@/types/orders";
import {
	BasketAddonItem,
	Addon,
	BasketItem,
	Service,
	AddonInputType,
	Package,
	BasketAddonItemWithoutType,
} from "@/types";

export function calculateOrderItemRemaining(pkg: Package, item: OrderItem, orderCreatedAt: Date) {
	const date = moment(orderCreatedAt);

	const dueDate = moment(item.createdAt).add(pkg.dueDays, "days");
	if (dueDate === undefined) return 100;

	const isPastDue = moment().isAfter(dueDate);
	const timeRemaining = moment.duration(Math.abs(dueDate.diff(moment())));
	const totalDuration = moment.duration(Math.abs(date.diff(dueDate)));

	if (isPastDue) return 100;

	const remainingMilliseconds = timeRemaining.asMilliseconds();
	const totalMilliseconds = totalDuration.asMilliseconds();

	return ((totalMilliseconds - remainingMilliseconds) / totalMilliseconds) * 100;
}

export function calculateAddonTotal(
	addonPrice: number,
	type: AddonInputType | null,
	value: BasketAddonItem["value"],
) {
	const x = deserializeAddonValue(type, value);
	return addonPrice * (typeof x === "boolean" ? (x as unknown as number) : x);
}

export function calculateAddonsTotal(
	addons: [string, BasketAddonItemWithoutType][],
	data: Addon[],
) {
	return addons.reduce((total, [addonId, { type, value }]) => {
		const { amount, inputType } = data.find((x) => x._id === addonId)!;

		/**
		 * Note(Curstantine):
		 * Number fields serialize empty fields as NaN, these should not be counted.
		 */
		const realValue = inputType === AddonInputType.number && Number.isNaN(value) ? 0 : value;
		return total + calculateAddonTotal(amount, type ?? inputType, realValue);
	}, 0);
}

export function calculatePackageTotal(
	pkg: Package,
	addonData: Addon[],
	selectedAddons: [string, BasketAddonItem][],
) {
	const addonTotal = calculateAddonsTotal(selectedAddons, addonData);

	return {
		addonTotal: addonTotal,
		packageTotal: pkg.price,
	};
}

export function calculateBasketTotal(items: BasketItem[], services: Service[]) {
	const servicesKeyed = Object.fromEntries(services.map((service) => [service.id, service]));

	return items.reduce((total, item) => {
		const service = servicesKeyed[item.serviceId];
		const selectedPkg = service.packages.find((x) => x._id === item.packageId)!;
		const { addonTotal, packageTotal } = calculatePackageTotal(
			selectedPkg,
			service.addons,
			Object.entries(item.addons),
		);

		return total + addonTotal + packageTotal;
	}, 0);
}

export function calculateOrderItemsTotal(items: OrderItem[]) {
	return items.reduce((total, { serviceId: service, package: pkg, addons }) => {
		if (service === null) {
			throw new Error("Service data is null. This service is mostly likely deleted");
		}

		const selectedPkg = service.packages.find((x) => x._id === pkg.packageId)!;
		const addonIds: [string, BasketAddonItemWithoutType][] = addons.map((x) => [
			x.addonId,
			{ value: x.quantity },
		]);

		const addonsTotal = calculateAddonsTotal(addonIds, service.addons);
		return total + selectedPkg.price * pkg.quantity + addonsTotal;
	}, 0);
}

export function transformBasketToOrderCreateItems({
	serviceId,
	addons,
	packageId,
	quantity,
}: BasketItem): OrderCreate["items"][0] {
	const addonEntries = Object.entries(addons);
	const adx = [];

	for (let i = 0; i < addonEntries.length; i++) {
		const [addonId, { type, value }] = addonEntries[i];

		const serializedValue: number =
			type === AddonInputType.number ? (value as number) : Number(value as boolean);

		if (Number.isNaN(serializedValue)) {
			console.error(
				`Addon '${addonId}' is being skipped due to serializedValue being NaN (type: ${type}, outcome of: ${value})`,
			);
			continue;
		}

		// Note(Curstantine): Backend only handles truthy addons.
		// Since boolean -> false gets transformed to 0, we can safely catch all falsy addons here and skip them.
		if (serializedValue === 0) continue;

		adx.push({ addonId, quantity: serializedValue });
	}

	return {
		serviceId,
		package: { packageId, quantity },
		addons: adx,
	};
}

export type SingleOrder = {
	id: any;
	summary: string;
	category: any;
	name: string;
	order: string;
	orderId: string;
	date: Date;
	dueBy: string;
	status: string;
	progress: any;
	files: string[];
	submissions: string[];
};

export const transformSingleOrderData = (order: Order) => {
	const orderId = order.id;
	const orderStatus = order.orderStatus;
	const userName = `${order.userId?.firstName} ${order.userId?.lastName}`;
	const orderDate = new Date(order.createdAt);
	const orderDueBy = new Date(order.createdAt);
	const items: SingleOrder[] = order.items?.map((item: any) => {
		const itemId = item._id;
		const itemTitle = item.serviceId?.title;
		const itemCategory = item.serviceId?.categoryId.title;
		const orderProgress = item.progress;

		return {
			id: itemId,
			summary: `${itemTitle} - ${
				item.serviceId?.packages.find((p: any) => p._id == item.package.packageId)?.title
			}, ${item.addons.length > 0 ? "Express delivery" : ""}`,
			category: itemCategory,
			name: userName,
			order: "#" + orderId.slice(0, 4),
			orderId: orderId,
			date: orderDate,
			dueBy: item.serviceId?.dueDays
				? moment(item.createdAt).add(item.serviceId?.dueDays, "days").format("DD/MM/YYYY")
				: "-",
			status: orderStatus,
			progress: orderProgress,
			files: order.files,
			submissions: order.submissions,
		};
	});

	return items[0];
};

export async function fetchFilesFromUrls(urls: string[]): Promise<File[]> {
	const filePromises = urls.map(async (url) => {
		const response = await fetch(url);
		const blob = await response.blob();
		// Extracting filename from the URL
		const filename = urlToFileName(url);
		return new File([blob], filename);
	});

	return Promise.all(filePromises);
}

export const removeUUIDv4Prefix = (__filename: string) => {
	if (typeof __filename !== "string") {
		return __filename;
	}

	const uuidv4Regex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}-/i;

	return __filename.split("/")[__filename.split("/").length - 1].replace(uuidv4Regex, "");
};

export const extractFromS3Url = (url: string) => {
	if (!url) {
		return url;
	}
	const s3Prefix = `https://${process.env.NEXT_PUBLIC_AWS_BUCKET_NAME}.s3.${process.env.NEXT_PUBLIC_AWS_REGION}.amazonaws.com/`;
	// Check if the URL starts with the expected S3 prefix
	if (url.startsWith(s3Prefix)) {
		return url.replace(s3Prefix, "");
	}
	// If it doesn't start with the expected S3 prefix, return the original URL.
	return url;
};

export const urlToFileName = (url: string): string => {
	return removeUUIDv4Prefix(extractFromS3Url(url));
};
